import React, { useEffect, useRef, memo } from 'react';
import { Box, Chip, ListItem, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import imageNotFound from '../../../assets/card-image/product-not-found.jpg';
import { capitalizeFirst, getHighestDiscount } from '../../../hooks/utilsFn/utilsFn';

function SingleSearchResult(props) {
  const { slug, name, categories, denominations, productImages, brand } = props.item || {};
  const { searchTerm, isSelected } = props;
  const resultRef = useRef(null);

  useEffect(() => {
    if (isSelected && resultRef.current) {
      resultRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [isSelected]);

  const userDiscounts = denominations
    ?.flatMap(item => item?.userProductDenominationDiscounts || [])
    ?.map(item => Number(item.discountValue))
    ?.filter(value => !isNaN(value));
  const discount = getHighestDiscount(denominations);
  const Discount =
    userDiscounts?.length > 0
      ? Math.max(...userDiscounts)
      : discount;

  const discountType = denominations?.[0]?.cashbackOrDiscount || 'DISCOUNT';
  const symbol = "%";
  const categoryName = categories?.[0]?.name || brand?.name || '';
  const imgUrl = productImages?.[0]?.url || categories?.[0]?.logo || imageNotFound;

  return (
    <NavLink
      ref={resultRef}
      to={`/card-details/${slug}`}
      style={{
        display: "flex",
        justifyContent: "center",
        textDecoration: "none",
        width: "100%",
      }}
    >
      <ListItem
        sx={{
          backgroundColor: isSelected ? "rgba(228, 193, 143, 0.18)" : "transparent",
          borderLeft: isSelected
            ? "3px solid #CF9D2E"
            : "3px solid transparent",
          "&:hover": {
            backgroundColor: "rgba(228, 193, 143, 0.12)",
          },
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 1.5, sm: 2 },
          py: 1.2,
          width: "100%",
          transition: "all 0.18s ease-in-out",
        }}
      >
        {/* Left Side: Thumbnail + Details */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
            flex: 1,
            mr: 1,
          }}
        >
          <Box
            sx={{
              width: { xs: 46, sm: 52 },
              height: { xs: 34, sm: 38 },
              borderRadius: "8px",
              overflow: "hidden",
              bgcolor: "#f9f9f9",
              border: "1px solid rgba(0,0,0,0.06)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={imgUrl}
              alt={name}
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = imageNotFound;
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: "12.5px", sm: "14px" },
                fontWeight: 600,
                color: "#1a1a1a",
                fontFamily: "Be Vietnam Pro",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
              }}
            >
              {(() => {
                const trimmed = searchTerm ? searchTerm.trim() : "";
                if (!trimmed || !name) return name;
                const tokens = trimmed
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((t) => t.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
                if (tokens.length === 0) return name;
                const regex = new RegExp(`(${tokens.join('|')})`, 'gi');
                const parts = name.split(regex);
                return parts.map((part, index) =>
                  regex.test(part) ? (
                    <span
                      key={index}
                      style={{
                        backgroundColor: "rgba(228, 193, 143, 0.45)",
                        color: "#8D5A24",
                        fontWeight: 700,
                        padding: "1px 4px",
                        borderRadius: "4px",
                        fontFamily: "Be Vietnam Pro",
                      }}
                    >
                      {part}
                    </span>
                  ) : (
                    part
                  )
                );
              })()}
            </Typography>

            {categoryName && (
              <Typography
                sx={{
                  fontSize: { xs: "11px", sm: "11.5px" },
                  color: "#6B7280",
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 400,
                  mt: 0.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {categoryName}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Side: Discount Badge + Arrow */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {Discount > 0 && (
            <Chip
              icon={<LocalOfferOutlinedIcon sx={{ "&&": { fontSize: 12, color: "#007411" } }} />}
              label={`${Discount}${symbol} ${capitalizeFirst(discountType)}`}
              size="small"
              sx={{
                backgroundColor: "rgba(0, 116, 17, 0.08)",
                border: "1px solid rgba(0, 116, 17, 0.25)",
                fontWeight: 600,
                color: "#007411",
                height: 24,
                fontSize: { xs: "10.5px", sm: "11.5px" },
                fontFamily: "Be Vietnam Pro",
                borderRadius: "6px",
                "& .MuiChip-label": {
                  px: 0.8,
                },
              }}
            />
          )}

          <ArrowForwardIosIcon
            sx={{
              fontSize: 12,
              color: "#9CA3AF",
              display: { xs: "none", sm: "block" },
            }}
          />
        </Box>
      </ListItem>
    </NavLink>
  );
}

export default memo(SingleSearchResult);
